import * as React from "react";
import {Box, Chip, Typography} from "@mui/material";
import i18n from "../../i18n";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTH_KEYS = [
    "common.month.1", "common.month.2", "common.month.3", "common.month.4",
    "common.month.5", "common.month.6", "common.month.7", "common.month.8",
    "common.month.9", "common.month.10", "common.month.11", "common.month.12",
];

/**
 * Converts a periodKey into a human-readable date range.
 * Supports both "M-YYYY" (e.g. "1-2026") and "YYYY-M" (e.g. "2026-1") formats.
 * Result: "1 January 2026 – 31 January 2026"
 */
export function formatPeriodDateRange(periodKey: string): string {
    const parts = periodKey.split("-");
    if (parts.length < 2) return periodKey;

    // Detect format: if first part is 4 digits → YYYY-M, otherwise M-YYYY
    let month: number;
    let year: number;
    if (parts[0].length === 4) {
        year = Number.parseInt(parts[0], 10);
        month = Number.parseInt(parts[1], 10);
    } else {
        month = Number.parseInt(parts[0], 10);
        year = Number.parseInt(parts[1], 10);
    }

    if (Number.isNaN(month) || Number.isNaN(year) || month < 1 || month > 12) return periodKey;
    const monthName = i18n.t(MONTH_KEYS[month - 1]);
    const lastDay = new Date(year, month, 0).getDate(); // day 0 of next month = last day of current month
    return `1 ${monthName} ${year} – ${lastDay} ${monthName} ${year}`;
}

/**
 * Converts a periodKey into a short "Month Year" label, e.g. "Shtator 2026".
 * Supports both "M-YYYY" and "YYYY-M" formats, same as formatPeriodDateRange.
 */
export function formatPeriodMonthYear(periodKey: string): string {
    const parts = periodKey.split("-");
    if (parts.length < 2) return periodKey;

    let month: number;
    let year: number;
    if (parts[0].length === 4) {
        year = Number.parseInt(parts[0], 10);
        month = Number.parseInt(parts[1], 10);
    } else {
        month = Number.parseInt(parts[0], 10);
        year = Number.parseInt(parts[1], 10);
    }

    if (Number.isNaN(month) || Number.isNaN(year) || month < 1 || month > 12) return periodKey;
    return `${i18n.t(MONTH_KEYS[month - 1])} ${year}`;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PeriodDateRangeBadgeProps {
    /** The period key in "M-YYYY" format, e.g. "1-2026". If null/undefined, renders nothing. */
    readonly periodKey: string | null | undefined;
    /** Controls border/text color: green when open, red otherwise. */
    readonly isOpen: boolean;
    /** Optional status label shown as a Chip below the date range (e.g. "OPEN" or "LOCKED"). */
    readonly statusLabel?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Displays the full date range of a payroll period as a large bordered badge.
 * - Green border + text when the period is open.
 * - Red border + text when the period is locked/closed.
 * - Right-aligned on desktop, full-width centered on mobile.
 */
export default function PeriodDateRangeBadge({
                                                 periodKey,
                                                 isOpen,
                                                 statusLabel,
                                             }: PeriodDateRangeBadgeProps): React.ReactElement | null {
    if (!periodKey) return null;

    const color = isOpen ? "#2e7d32" : "#c62828";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: {xs: "column", sm: "row"},
                justifyContent: {xs: "flex-start", sm: "flex-end"},
                alignItems: {xs: "stretch", sm: "center"},
            }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 1.5,
                    border: `2px solid ${color}`,
                    borderRadius: 2,
                    color,
                    width: {xs: "100%", sm: "auto"},
                    textAlign: {xs: "center", sm: "right"},
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: {xs: "center", sm: "flex-end"},
                    gap: 0.75,
                }}
            >
                <Typography
                    variant="h4"
                    component="span"
                    sx={{
                        fontWeight: 600,
                        fontSize: "1.4rem",
                        color: "inherit",
                    }}
                >
                    {formatPeriodDateRange(periodKey)}
                </Typography>
                {statusLabel && (
                    <Chip
                        label={statusLabel}
                        size="small"
                        sx={{
                            backgroundColor: color,
                            color: "#fff",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            alignSelf: {xs: "center", sm: "flex-end"},
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}

